import React, { Component } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import {
  message,
  Form,
  Input,
  Button,
  Checkbox,
  Select,
  AutoComplete,
  Tooltip,
  Alert,
  Divider,
} from 'antd';
import DatePicker from '../DatePicker';
import { InfoCircleOutlined } from '@ant-design/icons';
import ModalWrapper from './ModalWrapper';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../chartActions/replyTiffWarning';
import BoundChartChip from './BoundChartChip';
import PatientMatchSearch from './PatientMatchSearch';
import { buildMissingMessage } from './chartModalHelpers';
import './chartModal.scss';
import ReferringProviderAutocomplete from '../autocomplete/ReferringProviderAutocomplete';
import { taskCreatedByStamp, buildTaskTemplateNotes } from '../../utils/taskTemplateNotes';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import { captureAnalytics } from '../../actions/preload';
import { verifyFeatureAccess } from '../../global/featureFlags';
import {
  caretrackerEmr,
  caretrackerAccessOrg,
  categoryRequiredEmrs,
  caretrackerProviders,
  priorityOptions,
  taskOptions,
  taskDetailsOptions,
  authorizationTypeOptions,
} from './constants';
import { PREPOST_PRACTICE_OPTIONS, PREPOST_TENNESSEE_PRACTICE } from './prePostPractice';

const EMA_RESULTS_METADATA_ORG_ID = 4799;

// Section heading + AI provenance pill for the patient-first EMA/Kareo layout.
const SECTION_HEADING_STYLE = {
  display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '14px', margin: '4px 0 12px', color: '#1f1a2b',
};
const AI_PILL_STYLE = {
  marginLeft: 8, fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5e3999', background: '#efe9f7', borderRadius: '999px', padding: '2px 8px',
};

export class AutoUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fileName: props.modalType.data.fileName,
      receiverType: props.modalType.data.receiverType,
      documentCategory: props.modalType.data.documentCategory || '',
      documentCategories: [],
      providerName: props.modalType.data.providerName,
      createTaskAfterUpload: localStorage.createTaskAfterUpload,
      patientName: this.props.modalType.data.patientName || '',
      patientFirstName: this.props.modalType.data.patientFirstName || '',
      patientMiddleName: this.props.modalType.data.patientMiddleName || '',
      patientLastName: this.props.modalType.data.patientLastName || '',
      apiCredentials: this.props.modalType.data.apiCredentials,
      patientDOB: this.props.modalType.data.patientDOB || '',
      patientMrn: '',
      selectedEmrPatientId: null,
      assignTo: undefined,
      searchedTemplates: null,
      emaTaskTemplates: null,
      searchedNames: null,
      allProviderNames: [],
      task: '',
      searchedTasks: '',
      taskDetails: '',
      dueDate: '',
      dateOfService: props.modalType.data.dateOfService
        ? moment(props.modalType.data.dateOfService, 'YYYY-MM-DD').format('MM/DD/YYYY')
        : '',
      documentType: '',
      careTrackerProvider: '',
      taskDetailsSearchOptions: null,
      notes: taskCreatedByStamp(),
      priority: 'Normal',
      noTaskCreation: this.props.modalType.data.noTaskCreation,
      practiceName: null,
      value: '',
      facilityName: '',
      facilityNames: [],
      searchedFacilities: [],
      icd10Codes: props.modalType.data.icdCodes ? (Array.isArray(props.modalType.data.icdCodes) ? props.modalType.data.icdCodes : props.modalType.data.icdCodes.split(',').map((code) => code.trim())) : [],
      marketingRep: props.modalType.data.marketingRep || '',
      orderingProvider: '',
      showNameMismatchWarning: false,
      initialParsedName: '',
      // Initialize authorization fields from AI-extracted data
      // AI dates come in YYYY-MM-DD format, convert to MM/DD/YYYY for DatePicker
      // EMA users always start with authorization checkbox unchecked
      addAuthorization: localStorage.m8_auto_upload_emr === 'EMA' ? false : !!(props.modalType.data.authorizationNumber),
      authorizationType: props.modalType.data.authorizationNumber ? 'Authorization' : '',
      authorizationNumber: props.modalType.data.authorizationNumber || '',
      authorizationDescription: props.modalType.data.authorizationDescription || '',
      authorizationActiveStatus: true,
      authorizationUnlimitedVisits: !props.modalType.data.authorizationNumberOfVisits && !!(props.modalType.data.authorizationFromDate && props.modalType.data.authorizationToDate),
      authorizationNumberOfVisits: props.modalType.data.authorizationNumberOfVisits ? String(props.modalType.data.authorizationNumberOfVisits) : '',
      authorizationFromDate: props.modalType.data.authorizationFromDate ? moment(props.modalType.data.authorizationFromDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      authorizationToDate: props.modalType.data.authorizationToDate ? moment(props.modalType.data.authorizationToDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      authorizationNote: '',
      authorizationReferringProvider: '',
      canExtractAuthorization: false,
    };

    // Calculate initial name mismatch - this is determined once and never changes
    const normalizeSpaces = (str) => str ? str.trim().replace(/\s+/g, ' ') : '';
    const initialConstructedName = [
      this.props.modalType.data.patientFirstName,
      this.props.modalType.data.patientMiddleName,
      this.props.modalType.data.patientLastName,
    ].filter(Boolean).join(' ').trim();
    const normalizedConstructedName = normalizeSpaces(initialConstructedName);
    const normalizedOriginalName = normalizeSpaces(this.props.modalType.data.patientName);

    this.state.initialParsedName = initialConstructedName;
    this.state.showNameMismatchWarning = this.props.modalType.data.patientName &&
                                          normalizedConstructedName &&
                                          normalizedOriginalName &&
                                          normalizedConstructedName !== normalizedOriginalName;

    this.state.searchedTasks = taskOptions;
    this.state.taskDetailsSearchOptions = taskDetailsOptions;
    this.formRef = React.createRef();
  }

  async componentDidMount() {
    const {
      getDocumentCategories,
      getEmaTaskProviders,
      getKareoTaskProviders,
      getTaskTemplatesByQuery,
      getMarketingRepresentatives,
      modalType: { data: { sendToken } },
    } = this.props;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;

    await getDocumentCategories();

    // Fetch appropriate providers based on EMR type
    if (autoUploadEmr === 'Kareo') {
      await getKareoTaskProviders(sendToken);
    } else {
      await getEmaTaskProviders(sendToken);
    }

    await getMarketingRepresentatives();

    // Get provider names from the appropriate source
    let allProviders = [];
    if (autoUploadEmr === 'Kareo') {
      // Kareo providers are objects with {name, id}
      allProviders = this.props.allKareoTaskProviders || [];
    } else {
      // EMA providers are objects with {name, is_provider}
      allProviders = this.props.allEmaTaskProviders || [];
    }

    // For display, extract names
    const providerNames = allProviders.map((p) => {
      return typeof p === 'string' ? p : p?.name;
    }).filter(Boolean);

    this.setState({
      fileName: this.props.modalType.data.fileName,
      providerName: this.props.modalType.data.providerName,
      searchedNames: providerNames,
      allProviderNames: providerNames,
      documentCategories: this.props.allDocumentCategories.categories,
    }, () => {
      this.prefillOrderingProvider(providerNames, this.props.modalType.data.documentCategory);
    });
    if (providerNames.length === 1) {
      let newAssignTo = providerNames[0];
      if (autoUploadEmr === 'EMA') {
        // Set assign to as array
        newAssignTo = [newAssignTo];
      }
      this.setState({ assignTo: newAssignTo });
    }
    const query = `?email=${localStorage.getItem('m8_uid')}`;
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      searchedTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });
    const { patientName, patientFirstName, patientLastName } = this.state;
    if (!(patientFirstName || patientLastName) && patientName) {
      // Handle edge case where the patient_name has 2 surnames (this is common for Spanish names)
      if (patientName.includes(',') && patientName.split(',')[0].trim().includes(' ') && patientName.split(',')[0].trim().split(' ').length > 1) {
        const parsedNames = this.parseName(patientName);
        this.setState({
          patientFirstName: parsedNames.firstName,
          patientLastName: parsedNames.lastName,
        });
      } else {
        const parsedName = parseFullName(patientName);
        this.setState({
          patientFirstName: parsedName.first,
          patientMiddleName: parsedName.middle,
          patientLastName: parsedName.last,
        });
      }
    }
    const orgName = localStorage.m8_org;
    if (orgName === 'PrePost+') {
      this.setState({ practiceName: PREPOST_TENNESSEE_PRACTICE });
    }

    if (autoUploadEmr === 'EMA' && this.state.apiCredentials) {
      try {
        const response = await axios.get('/api/v1/emr_facilities');
        const res = await response.data;

        this.setState({
          facilityNames: res.facilities,
          searchedFacilities: res.facilities,
        });
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    }

    // Check feature flag for authorization extraction
    const canExtractAuthorization = await verifyFeatureAccess('extract_authorization_details');
    this.setState({ canExtractAuthorization });
  }

  isResultsCategory = (category = '') => (category || '').startsWith('Results - ');

  isScopedManualResultsFlow = (category = this.state.documentCategory) => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const modalData = this.props.modalType?.data || {};
    return autoUploadEmr === 'EMA'
      && Number(modalData.currentOrganizationId) === EMA_RESULTS_METADATA_ORG_ID
      && this.isResultsCategory(category);
  };

  // EMA exposes an ordering-provider dropdown for any document filed into its
  // Results tab, which every "Results - *" category routes to.
  shouldShowOrderingProviderField = (category = this.state.documentCategory) => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    if (autoUploadEmr !== 'EMA') return false;
    return (category || '').toLowerCase().startsWith('results - ');
  };

  shouldShowPerformedDateField = (category = this.state.documentCategory) => (
    this.isScopedManualResultsFlow(category)
  );

  parseProviderNameParts = (name) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      return { first: '', last: '' };
    }

    if (trimmedName.includes(',')) {
      const [lastName = '', firstName = ''] = trimmedName.split(',').map((part) => part.trim());
      return {
        first: firstName.split(/\s+/)[0].toLowerCase(),
        last: lastName.toLowerCase(),
      };
    }

    const parsedName = parseFullName(trimmedName);
    const fallbackParts = trimmedName.split(/\s+/);
    return {
      first: (parsedName.first || fallbackParts[0] || '').toLowerCase(),
      last: (parsedName.last || fallbackParts[fallbackParts.length - 1] || '').toLowerCase(),
    };
  };

  findMatchingOrderingProvider = (providerName, providerNames = []) => {
    if (!providerName || !providerNames.length) return null;

    const normalizedProviderName = providerName.trim().toLowerCase();
    const exactMatch = providerNames.find((name) => name.toLowerCase() === normalizedProviderName);
    if (exactMatch) return exactMatch;

    const target = this.parseProviderNameParts(providerName);
    if (!target.last) return null;

    const matches = providerNames.filter((name) => {
      const candidate = this.parseProviderNameParts(name);
      return candidate.last === target.last
        && (!target.first || candidate.first === target.first);
    });

    return matches.length === 1 ? matches[0] : null;
  };

  prefillOrderingProvider = (
    providerNames = this.getOrderingProviderNames(),
    documentCategory = this.state.documentCategory,
  ) => {
    if (
      !this.shouldShowOrderingProviderField(documentCategory)
      || !this.isScopedManualResultsFlow(documentCategory)
      || this.state.orderingProvider
      || !this.state.providerName
    ) {
      return;
    }

    const matchedProvider = this.findMatchingOrderingProvider(this.state.providerName, providerNames);
    if (matchedProvider) {
      this.setState({ orderingProvider: matchedProvider }, () => {
        if (this.formRef.current) {
          this.formRef.current.setFieldsValue({
            orderingProvider: matchedProvider,
          });
        }
      });
    }
  };

  // Helper to check if we should clear patient selection when fields are modified.
  // A selected chart is bound by id/MRN; editing the name or DOB must release that
  // binding so the document can never file to a chart that disagrees with the
  // visible fields. Applies to both Tebra (id) and EMA (MRN).
  shouldClearPatientSelection = () => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    return this.state.selectedEmrPatientId !== null && (autoUploadEmr === 'Kareo' || autoUploadEmr === 'EMA');
  }

  handleUnbindPatient = () => {
    // Clear both chart-binding identifiers. For EMA, binding a chart sets patientMrn
    // to the chart's MRN, so leaving it would let a later submit still file the
    // document to the released chart. Name/DOB stay so the re-search can prefill.
    this.setState({ selectedEmrPatientId: null, patientMrn: '' });
  };

  handleFieldChange = (e) => {
    const { name, value } = e.target;
    const isPatientField = ['patientFirstName', 'patientMiddleName', 'patientLastName', 'patientMrn'].includes(name);

    // Editing any patient-identity field (name or MRN; DOB is handled in
    // handleDateChange) after binding a chart means the visible patient no longer
    // matches the bound chart, so release the binding. For EMA the binding IS the
    // MRN (handlePatientSelection sets patientMrn to the chart's MRN), so we must
    // also drop that stale MRN — otherwise submit still forwards it and the backend
    // files to the released chart (wrong-patient misfile). The one exception is
    // editing the MRN field itself, where the newly typed value must stand.
    // Document/task fields don't change the patient and intentionally keep the binding.
    if (isPatientField && this.shouldClearPatientSelection()) {
      this.setState({
        selectedEmrPatientId: null,
        ...(name !== 'patientMrn' && { patientMrn: '' }),
        [name]: value,
      });
    } else {
      this.setState({
        [name]: value,
      });
    }
  }

  handleNotesChange = (e) => {
    this.setState({ notes: e.target.value });
  }

  handleSelectionChange = (e, variable) => {
    this.setState({ [variable]: e });
  };

  handleDocumentCategoryChange = (documentCategory) => {
    this.setState({ documentCategory }, () => {
      this.prefillOrderingProvider();
    });
  };

  handleFacilitySearch = (value) => {
    if (value) {
      const filtered = this.state.facilityNames.filter(facility =>
        facility.name.toLowerCase().includes(value.toLowerCase())
      );
      this.setState({ searchedFacilities: filtered });
    } else {
      this.setState({ searchedFacilities: this.state.facilityNames });
    }
  };

  handleFacilityChange = (value) => {
    if (!value) {
      this.setState({ facilityName: '', facilityId: null });
      return;
    }
    const selectedFacility = this.state.searchedFacilities.find(facility => facility.name === value);
    this.setState({ facilityName: selectedFacility.name, facilityId: selectedFacility.id });
  };

  handleCreateTaskAfterUpload = async (e) => {
    if (!localStorage.createTaskAfterUpload) {
      localStorage.setItem('createTaskAfterUpload', true);
    } else {
      localStorage.removeItem('createTaskAfterUpload');
    }
    this.setState({ createTaskAfterUpload: e.target.checked }, () => {
      requestAnimationFrame(() => {
        const taskTemplatesSection = document.querySelector('[id="taskTemplates"]');
        if (taskTemplatesSection) {
          taskTemplatesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

  handleDateChange = (_, dateString) => {
    // Modifying DOB after binding a chart means the visible patient no longer matches
    // the bound chart: release the binding AND clear the bound chart's MRN. Leaving
    // patientMrn set would let submit still forward it and file to the released chart
    // (wrong-patient misfile), since the backend resolves an MRN-only lookup.
    if (this.shouldClearPatientSelection()) {
      this.setState({
        patientDOB: dateString,
        selectedEmrPatientId: null,
        patientMrn: '',
      });
    } else {
      this.setState({ patientDOB: dateString });
    }
  };

  handleDueDateChange = (_, dateString) => {
    this.setState({ dueDate: dateString });
  };

  handleDateOfServiceChange = (_, dateString) => {
    this.setState({ dateOfService: dateString });
  };

  handleAssignToSearch = (e) => {
    const allNames = this.state.allProviderNames || [];

    if (e) {
      const filtered = allNames.filter((name) => name.toLowerCase().includes(e.toLowerCase()));
      this.setState({ searchedNames: filtered });
    } else {
      this.setState({ searchedNames: allNames });
    }
  };

  handleTaskSearch = (e) => {
    if (e) {
      const filtered = this.taskOptions.filter(task => task.toLowerCase().includes(e.toLowerCase()));
      this.setState({ searchedTasks: filtered });
    } else {
      this.setState({ searchedTasks: this.taskOptions });
    }
  };

  handleSearchTaskDetails = (e) => {
    this.setState({ taskDetails: e });
    const filteredOptions = taskDetailsOptions.filter(option => (
      option.toLowerCase().includes(e.toLowerCase())));
    this.setState({ taskDetailsSearchOptions: filteredOptions });
  };

  handleTaskTemplateChange = (e) => {
    if (this.isKareoEmr()) {
      captureAnalytics('tebra_note_template_selection_changed', {
        action: e ? 'selected' : 'cleared',
        workflow: 'send_to_chart',
        modal_variant: 'redesigned',
      });
    }
    // if user clears out selection, we will reload the list of templates to choose from.
    if (!e) {
      this.handleTaskTemplateSearch();
      const stamp = taskCreatedByStamp();
      this.setState({
        notes: stamp,
        priority: undefined,
        taskDetails: undefined,
        task: undefined,
        assignTo: undefined,
      }, () => {
        this.formRef.current.setFieldsValue({
          notes: stamp,
          assignTo: undefined,
          priority: undefined,
          taskDetails: undefined,
          task: undefined,
        });
      });
      return;
    }
    const index = this.state.emaTaskTemplates.map(template => template.id).indexOf(e);
    const selectedTemplate = this.state.emaTaskTemplates[index];
    const assigtoArray = [...(selectedTemplate.assign_to_as_array || [])];
    if (assigtoArray.includes('Ordering Provider')) {
      assigtoArray.splice(assigtoArray.indexOf('Ordering Provider'), 1);
    }
    const newNotes = buildTaskTemplateNotes(selectedTemplate.notes);
    this.setState({
      priority: selectedTemplate.priority,
      taskDetails: selectedTemplate.task_details,
      task: selectedTemplate.task_type,
      assignTo: this.isKareoEmr() ? assigtoArray[0] : assigtoArray,
      notes: newNotes,
    }, () => {
      // Get form instance from ref
      this.formRef.current.setFieldsValue({
        priority: selectedTemplate.priority,
        taskDetails: selectedTemplate.task_details,
        task: selectedTemplate.task_type,
        assignTo: this.isKareoEmr() ? assigtoArray[0] : assigtoArray,
        notes: newNotes,
      });
    });
  };

  handleTaskTemplateSearch = (e) => {
    const unfilteredObjects = this.state.emaTaskTemplates;
    if (e) {
      const filtered = this.state.emaTaskTemplates.map(template => template.task_name).filter(option => (option.toLowerCase().includes(e.toLowerCase())));
      const filteredObject = [];
      filtered.forEach((template) => {
        const index = this.state.emaTaskTemplates.map(t => t.task_name).indexOf(template);
        filteredObject.push(this.state.emaTaskTemplates[index]);
      });
      this.setState({ searchedTemplates: filteredObject });
    } else {
      this.setState({ searchedTemplates: unfilteredObjects });
    }
  };

  // Guards against double-click firing two upload requests. The flag is an instance
  // property (not state) so the re-entry check is synchronous within a single click
  // burst, before any await yields control back to a second click.
  handleSubmit = async () => {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      await this.performSubmit();
    } finally {
      this.isSubmitting = false;
    }
  };

  performSubmit = async () => {
    const {
      hideModal, triggerAutoUpload, triggerAutoUploadAndTaskCreation, modalType: { data: { sendToken } },
    } = this.props;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const {
      fileName,
      receiverType,
      documentCategory,
      providerName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientDOB,
      patientMrn,
      assignTo,
      task,
      taskDetails,
      dueDate,
      priority,
      notes,
      createTaskAfterUpload,
      noTaskCreation,
      practiceName,
      documentType,
      careTrackerProvider,
      dateOfService,
      facilityName,
      marketingRep,
      icd10Codes,
      orderingProvider,
      addAuthorization,
      authorizationType,
      authorizationNumber,
      authorizationDescription,
      authorizationActiveStatus,
      authorizationUnlimitedVisits,
      authorizationNumberOfVisits,
      authorizationFromDate,
      authorizationToDate,
      authorizationNote,
      authorizationReferringProvider,
      canExtractAuthorization,
    } = this.state;

    // Only send authorization data if feature flag is enabled AND user checked the checkbox
    const shouldSendAuthorization = addAuthorization && canExtractAuthorization;
    const shouldSendDateOfService = (
      autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg
    ) || this.shouldShowPerformedDateField(documentCategory);

    let formattedDOB = '';
    let formattedDueDate = '';
    let formattedDateOfService = '';
    const finalizedMarketingRep = this.props.allMarketingRepresentatives?.includes(marketingRep) ? marketingRep : null;
    if (patientDOB !== '') {
      const splitDate = patientDOB.split('/');
      formattedDOB = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }
    if (dueDate !== '') {
      const splitDate = dueDate.split('/');
      formattedDueDate = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }
    if (dateOfService !== '') {
      const splitDate = dateOfService.split('/');
      formattedDateOfService = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }

    let uploadResult;
    if (createTaskAfterUpload && !noTaskCreation) {
      uploadResult = await triggerAutoUploadAndTaskCreation({
        sendToken, receiverType, documentCategory, fileName, patientFirstName, patientLastName, patientMiddleName, patientDOB: formattedDOB, patientMrn, assignTo, task, taskDetails, dueDate: formattedDueDate, priority, notes, facilityName, orderingProvider,
        dateOfService: shouldSendDateOfService ? formattedDateOfService : '',
        fromReferralManagement: this.props.modalType?.data?.fromReferralManagement === true,
        authorizationType: shouldSendAuthorization ? authorizationType : '',
        authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
        authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
        authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
        authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
        authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
        authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
        authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
        authorizationNote: shouldSendAuthorization ? authorizationNote : '',
        authorizationReferringProvider: shouldSendAuthorization ? authorizationReferringProvider : '',
      });
    } else {
      const assignee = (autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg) ? careTrackerProvider : assignTo || providerName;
      uploadResult = await triggerAutoUpload({
        sendToken,
        receiverType,
        documentCategory,
        fileName,
        practitionerName: assignee,
        patientFirstName,
        patientLastName,
        patientMiddleName,
        patientDOB: formattedDOB,
        patientMrn,
        practiceName,
        documentType,
        dateOfService: shouldSendDateOfService ? formattedDateOfService : '',
        facilityName,
        marketingRep: finalizedMarketingRep,
        icd10Codes,
        orderingProvider,
        fromReferralManagement: this.props.modalType?.data?.fromReferralManagement === true,
        emrPatientId: this.state.selectedEmrPatientId,
        authorizationType: shouldSendAuthorization ? authorizationType : '',
        authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
        authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
        authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
        authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
        authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
        authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
        authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
        authorizationNote: shouldSendAuthorization ? authorizationNote : '',
        authorizationReferringProvider: shouldSendAuthorization ? authorizationReferringProvider : '',
      });
    }
    if (uploadResult?.canceled) return;
    hideModal();
    const { error, errorMessage } = this.props;
    if (!error) {
      // ENG-3012 TIFF deferral: partial success (visible TIFFs excluded from a
      // reply filing) shows an acknowledged warning instead of the ordinary
      // success toast.
      const tiffWarnings = extractReplyTiffWarnings(uploadResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({
          warnings: tiffWarnings,
          workflow: (createTaskAfterUpload && !noTaskCreation) ? 'legacy_upload_and_task' : 'legacy_send_to_chart',
        });
      } else {
        message.success({
          content: uploadResult?.repeatUploadAction === 'task_only'
            ? 'Task queued without uploading another document.'
            : 'Record will be uploaded shortly. We will notify you of any issues via email.',
          duration: 1.5,
        });
      }
      // Refresh the current records view so the row picks up the in-progress chart
      // status (chart_in_progress / auto_upload_in_progress) without a manual refresh.
      this.props.refreshCurrentRecords?.();
    } else {
      message.error({
        content: errorMessage,
      });
    }
  };

  getOrderingProviderNames() {
    const src = this.props.allEmaTaskProviders;
    if (!Array.isArray(src) || src.length === 0) return [];
    return src
      .filter((p) => p && p.is_provider)
      .map((p) => p.name)
      .filter(Boolean);
  }

  isKareoEmr() {
    return localStorage.getItem('m8_auto_upload_emr') === 'Kareo';
  }

  parseName(name) {
    let firstName = '';
    let lastName = '';
    const parts = name.split(',');
    lastName = parts[0].trim();
    firstName = parts[1].trim();

    // Handle edge cases for Spanish names with multiple surnames
    if (lastName.split(' ').length > 1 && !lastName.includes('-')) {
      const lastNames = lastName.split(' ');
      lastName = `${lastNames[0]} ${lastNames[1]}`;
    }

    return { firstName, lastName };
  }

  renderAssignToSelect() {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const noProviderMessage = 'Contact your Admin to add EMR Providers';
    const providers = autoUploadEmr === 'Kareo'
      ? this.props.allKareoTaskProviders
      : this.props.allEmaTaskProviders;
    const noProvidersAvailable = !providers || providers.length === 0;
    return (
      <Form.Item
        name="assignTo"
        label={noProvidersAvailable
          ? (
            <div>
              Assign to EMR Staff or Staff Group
              <Tooltip title="Please contact your Admin to add Providers">
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
            </div>
          ) : (
            <div>
              Assign to EMR Staff or Staff Group
            </div>
          )}
        style={{ flex: 1 }}
      >
        <Select
          mode={autoUploadEmr === 'EMA' ? 'multiple' : undefined}
          showSearch
          placeholder="Select a provider"
          optionFilterProp="children"
          onChange={e => this.handleSelectionChange(e, 'assignTo')}
          onSearch={this.handleAssignToSearch}
          filterOption={false}
          value={this.state.assignTo}
          allowClear
          notFoundContent={noProviderMessage}
        >
          {this.state.searchedNames && this.state.searchedNames.map((e, index) => (
            <Select.Option key={e + index} value={e}>
              {e}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  renderDocumentTypeSelect() {
    return (
      <Form.Item
        name="documentType"
        label="Document Type"
        style={{ flex: 1 }}
      >
        <Select placeholder="Select Document Type" onChange={e => this.handleSelectionChange(e, 'documentType')}>
          <Select.Option value="Clinical">Clinical</Select.Option>
          <Select.Option value="Patient">Patient</Select.Option>
        </Select>
      </Form.Item>
    );
  }

  renderCareTrackerDocumentTitleAndDateOfService() {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Form.Item
          hasFeedback
          label="Document Title"
          name="fileName"
          style={{ flex: 1 }} // Added flex to ensure it takes available space
        >
          <Input
            placeholder="Document Title"
            name="fileName"
            onChange={this.handleFieldChange}
          />
        </Form.Item>

        <Form.Item
          label="Date of Service"
          style={{ flex: 0.3 }}
        >
          <DatePicker
            onChange={this.handleDateOfServiceChange}
            name="dateOfService"
            format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
            value={this.state.dateOfService && moment(this.state.dateOfService, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
          />
        </Form.Item>
      </div>
    );
  }

  renderCareTrackerProviderSelect() {
    return (
      <Form.Item
        name="Provider"
        label="Provider"
        style={{ flex: 1 }}
      >
        <Select
          placeholder="Select Provider"
          showSearch
          optionFilterProp="children"
          allowClear
          onChange={e => this.handleSelectionChange(e, 'careTrackerProvider')}
        >
          {caretrackerProviders.map(provider => (
            <Select.Option key={provider} value={provider}>
              {provider}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  renderPatientName() {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Form.Item
            label="Patient First Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="First name"
              name="patientFirstName"
              value={this.state.patientFirstName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
          <Form.Item
            label="Patient Middle Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="Middle name"
              name="patientMiddleName"
              value={this.state.patientMiddleName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
          <Form.Item
            label="Patient Last Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="Last name"
              name="patientLastName"
              value={this.state.patientLastName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
        </div>
      </div>
    );
  }

  renderDocumentCategorySelect(documentCategories, documentCategory) {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const categorySelect = (
      <Form.Item
        name="documentCategory"
        label={autoUploadEmr === 'EMA' ? <span>Document Category <span style={{ color: 'red' }}>*</span></span> : 'Document Category'}
        style={{ flex: 1 }}
        rules={autoUploadEmr === 'EMA' ? [
          () => ({
            validator(_, name) {
              if (name && name.trim() !== '') return Promise.resolve();
              return Promise.reject('Category cannot be blank');
            },
          }),
        ] : []}
      >
        <Select
          label="Document Category"
          placeholder="Select a document category"
          allowClear
          showSearch
          onChange={this.handleDocumentCategoryChange}
          value={documentCategory}
        >
          {dedupeDocumentCategories(documentCategories).map((emrCategory) => (
            <Select.Option key={emrCategory.id} value={emrCategory.emr_category}>
              {emrCategory.emr_category}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );

    const showOrderingProviderField = this.shouldShowOrderingProviderField(documentCategory);
    const showPerformedDateField = this.shouldShowPerformedDateField(documentCategory);
    const isEMA = autoUploadEmr === 'EMA';
    if ((!showOrderingProviderField && !showPerformedDateField) || !isEMA) return categorySelect;

    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        {categorySelect}
        {showOrderingProviderField && (
          <Form.Item
            name="orderingProvider"
            label={(
              <span>
                {this.isScopedManualResultsFlow(documentCategory) ? 'Provider' : 'Ordering Provider'}
                {localStorage.m8_auto_upload_emr === 'EMA' && (
                  <Tooltip title="Start typing to search and select a matching provider from your EMR. If our AI extracted the provider, we will try to preselect it for you. You can also leave this field blank.">
                    <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                  </Tooltip>
                )}
              </span>
            )}
            style={{ flex: 1 }}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select provider"
              onChange={(e) => this.handleSelectionChange(e, 'orderingProvider')}
              value={this.state.orderingProvider}
              allowClear
            >
              {this.getOrderingProviderNames().map((name) => (
                <Select.Option key={name} value={name}>{name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {showPerformedDateField && (
          <Form.Item
            label="Performed Date"
            style={{ flex: 0.7 }}
          >
            <DatePicker
              onChange={this.handleDateOfServiceChange}
              name="dateOfService"
              format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
              value={this.state.dateOfService && moment(this.state.dateOfService, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
            />
          </Form.Item>
        )}
      </div>
    );
  }

  handlePatientSelection = (record) => {
    // Validate required fields
    if (!record || !record.name || !record.dob) {
      return;
    }

    const isKareo = this.isKareoEmr();

    const fullName = record.name;
    const dob = record.dob;
    const mrn = record.mrn;
    const id = record.id;
    const patientName = fullName.trim();
    const patientMrn = mrn ? mrn.trim() : '';
    const formattedDOB = moment(dob.trim()).format('MM/DD/YYYY');

    // For Tebra: use patient ID, for EMA: use MRN
    const selectedEmrPatientId = isKareo ? id : mrn;

    const parsedName = parseFullName(patientName);

    this.setState({
      patientFirstName: parsedName.first || '',
      patientMiddleName: parsedName.middle || '',
      patientLastName: parsedName.last || '',
      patientMrn,
      patientDOB: formattedDOB,
      open: false,
      showNameMismatchWarning: false,
      selectedEmrPatientId,
    }, () => {
      // Sync form fields with updated state
      if (this.formRef.current) {
        this.formRef.current.setFieldsValue({
          patientDOB: moment(formattedDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']),
        });
      }
    });
  };

  renderPatientMRNandDOB(emr, patientMrn, patientDOB) {
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const isKareo = this.isKareoEmr();
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Form.Item
          label="Patient Date of Birth"
          style={{ flex: 1 }}
        >
          <DatePicker
            onChange={this.handleDateChange}
            name="patientDOB"
            format={dateFormat}
            defaultValue={patientDOB && moment(patientDOB, dateFormat)}
            value={patientDOB && moment(patientDOB, dateFormat)}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {!isKareo && (
          <Form.Item
            label={emr === 'ECW' ? 'Account Number (Optional)' : 'Patient MRN (Optional)'}
            style={{ flex: 1 }}
          >
            <Input
              placeholder={emr === 'ECW' ? 'Account Number' : 'Patient MRN'}
              name="patientMrn"
              defaultValue={patientMrn}
              value={patientMrn}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
        )}
      </div>
    );
  }

  handleICD10CodesChange = (value) => {
    this.setState({ icd10Codes: value });
  };

  handleMarketingRepChange = (value) => {
    this.setState({ marketingRep: value });
  };

  renderNameMismatchAlert() {
    if (!this.state.showNameMismatchWarning) return null;
    return (
      <Alert
        message="Patient Name Mismatch Detected"
        description={(
          <div>
            Please verify the patient&apos;s name before sending to chart. Due to a recent
            system migration, some previously edited values may be inconsistent.
            <br />
            <br />
            Extracted name:
            {' '}
            <span style={{ color: '#722ed1' }}>{this.state.initialParsedName}</span>
            <br />
            Updated name:
            {' '}
            <span style={{ color: '#722ed1' }}>{this.props.modalType.data.patientName}</span>
          </div>
        )}
        type="warning"
        showIcon
        style={{ marginBottom: '12px' }}
      />
    );
  }

  renderEmaDocumentTitle() {
    const isEMA = localStorage.m8_auto_upload_emr === 'EMA';
    return (
      <Form.Item
        hasFeedback
        label={isEMA ? <span>Document Title <span style={{ color: 'red' }}>*</span></span> : 'Document Title'}
        name="fileName"
        rules={[
          () => ({
            validator(_, name) {
              if (name && name.trim() !== '') return Promise.resolve();
              return Promise.reject('Name cannot be blank');
            },
          }),
        ]}
      >
        <Input
          placeholder="e.g. Lab results — June 2026"
          name="fileName"
          onChange={this.handleFieldChange}
        />
      </Form.Item>
    );
  }

  // Patient-first layout for the EMR-API EMRs (EMA / Kareo): the extracted patient
  // leads, the ModMed/Tebra match strip sits BELOW those same fields (and is
  // replaced by the bound chip once a chart is chosen), and the document fields
  // follow in their own section.
  renderEmaKareoLayout() {
    const {
      documentCategories, documentCategory, patientFirstName, patientMiddleName, patientLastName, patientMrn, patientDOB, apiCredentials, selectedEmrPatientId,
    } = this.state;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const isBound = selectedEmrPatientId !== null && selectedEmrPatientId !== undefined;
    // EMA supports patient search whether or not the org is on the EMA API: API orgs
    // hit the FHIR search and non-API orgs hit the EHR-Gateway scraper search, both
    // through /api/v1/ema_patients (which forks server-side on the org's credentials).
    // Kareo still requires API credentials, so leave its gating unchanged.
    const canSearchPatients = apiCredentials || autoUploadEmr === 'EMA';
    return (
      <>
        <div style={SECTION_HEADING_STYLE}>
          Patient
          <span style={AI_PILL_STYLE}>Extracted by AI — verify</span>
        </div>
        {this.renderNameMismatchAlert()}
        {isBound && (
          <BoundChartChip
            name={[patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ')}
            dob={patientDOB}
            mrn={patientMrn}
            onChange={this.handleUnbindPatient}
          />
        )}
        {this.renderPatientName()}
        {this.renderPatientMRNandDOB(autoUploadEmr, patientMrn, patientDOB)}
        {canSearchPatients && !isBound && (
          <PatientMatchSearch
            getEmaPatients={this.props.getEmaPatients}
            getTebraPatients={this.props.getTebraPatients}
            isKareo={this.isKareoEmr()}
            gatewayEmaSearch={autoUploadEmr === 'EMA' && !apiCredentials}
            firstName={patientFirstName}
            middleName={patientMiddleName}
            lastName={patientLastName}
            dob={patientDOB}
            onSelectPatient={this.handlePatientSelection}
          />
        )}
        <Divider style={{ margin: '14px 0 16px' }} />
        <div style={SECTION_HEADING_STYLE}>Document</div>
        {this.renderEmaDocumentTitle()}
        {this.renderDocumentCategorySelect(documentCategories, documentCategory)}
      </>
    );
  }

  render() {
    const { hideModal, isLoading } = this.props;
    const
      {
        fileName,
        documentCategories,
        documentCategory,
        providerName,
        patientFirstName,
        patientLastName,
        patientDOB,
        patientMrn,
        notes,
        assignTo,
        createTaskAfterUpload,
        noTaskCreation,
        practiceName,
        documentType,
        careTrackerProvider,
      } = this.state;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const isEmaOrKareo = autoUploadEmr === 'EMA' || autoUploadEmr === 'Kareo';
    const docTitleSupport = ['Kareo', 'OncoEMR', 'Nymbl', 'EMA', 'ECW', 'AllegianceMD', 'Elation'];
    const customContentStyle = {};
    const canCreateTaskAfterUpload = autoUploadEmr === 'EMA' || autoUploadEmr === 'Kareo';
    const taskCheckboxLabel = autoUploadEmr === 'Kareo' ? 'Create note after upload' : 'Create task after upload';
    const disableSubmitButton = () => {
      // Authorization validations (only when addAuthorization is checked and feature flag enabled) - EMA only
      const authorizationValidationsFailed = autoUploadEmr === 'EMA' && this.state.canExtractAuthorization && this.state.addAuthorization && (
        !this.state.authorizationType
        || !this.state.authorizationNumber
        || !this.state.authorizationDescription
        || (this.state.authorizationUnlimitedVisits
          ? (!this.state.authorizationFromDate || !this.state.authorizationToDate)
          : !this.state.authorizationNumberOfVisits
        )
      );

      if (this.state.createTaskAfterUpload && !noTaskCreation) {
        const isKareo = autoUploadEmr === 'Kareo';

        // Base requirements for all EMRs: need (first AND last name AND DOB) OR MRN
        const missingPatientInfo = (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '');
        const missingAssignee = (this.state.assignTo && this.state.assignTo.length === 0) || !this.state.assignTo;
        const missingCategory = autoUploadEmr === 'EMA' && !documentCategory;

        if (isKareo) {
          return missingPatientInfo || missingAssignee || missingCategory;
        }

        // Falsy check (not `=== ''`): clearing the Task Template sets these
        // to undefined, which would slip past a strict empty-string compare.
        return (
          missingPatientInfo
          || missingAssignee
          || !this.state.task
          || !this.state.taskDetails
          || missingCategory
          || authorizationValidationsFailed
        );
      }
      if (autoUploadEmr === 'EMA') {
        return (
          (docTitleSupport.includes(autoUploadEmr) ? fileName === '' : providerName === '')
          || !documentCategory
          || (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '')
          || authorizationValidationsFailed
        );
      }
      if (autoUploadEmr === 'ECW') {
        return (
          (((patientFirstName === '' || patientLastName === '') && patientDOB === '') && patientMrn === '')
          || !assignTo
          || !documentCategory
        );
      }
      if (autoUploadEmr === 'AdvancedMD') {
        return providerName === '' || providerName === null;
      }
      if (autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg) {
        return !careTrackerProvider || documentType === '' || !documentCategory;
      }
      return (
        (docTitleSupport.includes(autoUploadEmr) ? fileName === '' : providerName === '')
        || (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '')
      );
    };

    // Footer "what's left" summary for the EMA/Kareo path: names the still-missing
    // required fields when Submit is disabled. Mirrors disableSubmitButton above.
    const getMissingMessage = () => {
      const missing = [];
      const missingPatient = (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '');
      if (docTitleSupport.includes(autoUploadEmr) && fileName === '') missing.push('a document title');
      if (autoUploadEmr === 'EMA' && !documentCategory) missing.push('a document category');
      if (missingPatient) missing.push('the patient name and date of birth');
      if (createTaskAfterUpload && !noTaskCreation) {
        const missingAssignee = (this.state.assignTo && this.state.assignTo.length === 0) || !this.state.assignTo;
        if (missingAssignee) missing.push('a task assignee');
        if (autoUploadEmr !== 'Kareo') {
          if (!this.state.task) missing.push('a task type');
          if (!this.state.taskDetails) missing.push('task details');
        }
      }
      if (autoUploadEmr === 'EMA' && this.state.canExtractAuthorization && this.state.addAuthorization
        && (!this.state.authorizationType || !this.state.authorizationNumber || !this.state.authorizationDescription
          || (this.state.authorizationUnlimitedVisits
            ? (!this.state.authorizationFromDate || !this.state.authorizationToDate)
            : !this.state.authorizationNumberOfVisits))) {
        missing.push('the authorization details');
      }
      return buildMissingMessage(missing, 'to send.');
    };

    const submitDisabled = disableSubmitButton();
    const whatsLeft = (submitDisabled && isEmaOrKareo) ? getMissingMessage() : null;

    return (
      <div>
        <ModalWrapper
          customContentStyle={customContentStyle}
          width="860px"
          hideModal={hideModal}
          dismiss={isLoading ? null : 'Cancel'}
          action={this.handleSubmit}
          actionName="Submit"
          disabled={submitDisabled}
          isLoading={isLoading}
          modalTitle={isEmaOrKareo ? 'Send to chart' : 'Confirm document'}
          wrapClassName="chart-modal-wrap"
          footerNote={whatsLeft}
          isDraggable
        >
          <Form
            ref={this.formRef}
            layout="vertical"
            onFinish={this.handleSubmit}
            initialValues={{
              taskAfterUpload: createTaskAfterUpload, documentCategory, providerName, fileName, notes,
            }}
          >
            {!isEmaOrKareo && this.renderNameMismatchAlert()}
            {(docTitleSupport.includes(autoUploadEmr) && !isEmaOrKareo)
              && (
                <Form.Item
                  hasFeedback
                  label="Document Title Name"
                  name="fileName"
                  rules={[
                    () => ({
                      validator(_, name) {
                        if (name && name.trim() !== '') return Promise.resolve();
                        return Promise.reject('Name cannot be blank');
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder="Document Title Name"
                    name="fileName"
                    onChange={this.handleFieldChange}
                  />
                </Form.Item>
              )}
            {autoUploadEmr === 'ECW'
              && (
                <>
                  {this.renderPatientName()}
                  <Form.Item
                    label="Patient Date of Birth"
                    style={{ flex: 1 }}
                  >
                    <DatePicker
                      onChange={this.handleDateChange}
                      name="patientDOB"
                      format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                      defaultValue={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                      value={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  {this.renderPatientMRNandDOB(autoUploadEmr, patientMrn, patientDOB)}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 0.5 }}>
                      {this.renderDocumentCategorySelect(documentCategories, documentCategory)}
                    </div>
                    <div style={{ flex: 0.5 }}>
                      {this.renderAssignToSelect(true)}
                    </div>
                  </div>
                </>
              )}
            {(categoryRequiredEmrs.includes(autoUploadEmr) && !isEmaOrKareo)
              && this.renderDocumentCategorySelect(documentCategories, documentCategory)}
            {autoUploadEmr === 'AllegianceMD' && this.renderAssignToSelect(true)}
            {autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg && (
              <>
                {this.renderCareTrackerDocumentTitleAndDateOfService()}
                {this.renderDocumentTypeSelect()}
                {this.renderCareTrackerProviderSelect()}
              </>
            )}
            {autoUploadEmr === 'Brightree'
              && (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                    <Form.Item
                      label="Patient First Name"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="First name"
                        name="patientFirstName"
                        value={this.state.patientFirstName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Middle Name"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Middle name"
                        name="patientMiddleName"
                        value={this.state.patientMiddleName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Last Name"
                      style={{ flex: 1 }}
                    >
                      <Input
                        placeholder="Last name"
                        name="patientLastName"
                        value={this.state.patientLastName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Date of Birth"
                      style={{ flex: 1 }}
                    >
                      <DatePicker
                        onChange={this.handleDateChange}
                        name="patientDOB"
                        format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                        defaultValue={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                        value={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </div>
                  <Form.Item
                    label="ICD-10 Codes"
                  >
                    <Select
                      mode="tags"
                      name="icd10Codes"
                      placeholder="Enter ICD-10 codes (separated by commas)"
                      style={{ width: '100%' }}
                      onChange={this.handleICD10CodesChange}
                      tokenSeparators={[',']}
                      notFoundContent={null}
                      value={this.state.icd10Codes}
                      defaultValue={this.state.icd10Codes}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Marketing Representative"
                  >
                    <Select
                      placeholder="Select a marketing representative"
                      style={{ width: '100%' }}
                      onChange={this.handleMarketingRepChange}
                      value={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                      defaultValue={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                      allowClear
                      notFoundContent={
                        (!this.props.allMarketingRepresentatives || this.props.allMarketingRepresentatives.length === 0)
                          ? 'No marketing representatives found. Please contact your admin to add them.'
                          : null
                      }
                    >
                      {this.props.allMarketingRepresentatives && this.props.allMarketingRepresentatives.map((name) => (
                        <Select.Option key={name} value={name}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}
            {(autoUploadEmr === 'AdvancedMD')
              && (
                <>
                  {this.renderDocumentCategorySelect(documentCategories, documentCategory)}
                  <Form.Item
                    hasFeedback
                    label="Provider Name"
                    name="providerName"
                    rules={[
                      () => ({
                        validator(_, name) {
                          if (name && name.trim() !== '') return Promise.resolve();
                          return Promise.reject('Name cannot be blank');
                        },
                      }),
                    ]}
                    style={{ flex: 1 }}
                  >
                    <Input
                      placeholder="Last name, First name"
                      name="providerName"
                      onChange={this.handleFieldChange}
                    />
                  </Form.Item>
                </>
              )}

            {isEmaOrKareo && this.renderEmaKareoLayout()}

            {canCreateTaskAfterUpload && !noTaskCreation
              && (
                <Form.Item
                  style={{ display: 'flex', marginTop: 20 }}
                  name="taskAfterUpload"
                  valuePropName="checked"
                  onChange={this.handleCreateTaskAfterUpload}
                >
                  <Checkbox>{taskCheckboxLabel}</Checkbox>
                </Form.Item>
              )}

            {(localStorage.m8_org === 'PrePost+' && practiceName)
              && (
                <Form
                  initialValues={{
                    locationSelection: practiceName,
                  }}
                >
                  <Form.Item
                    style={{ display: 'flex', marginTop: 20 }}
                    name="locationSelection"
                    label="Location"
                  >
                    <Select
                      onChange={value => this.setState({ practiceName: value })}
                      value={practiceName}
                      options={PREPOST_PRACTICE_OPTIONS}
                    >
                    </Select>
                  </Form.Item>
                </Form>
               )
            }

            {(createTaskAfterUpload && !noTaskCreation)
              && (
                <>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {['EMA', 'Kareo'].includes(autoUploadEmr)
                      && (
                        <Form.Item
                          label={autoUploadEmr === 'Kareo' ? 'Note Templates' : 'Task Templates'}
                          name="taskTemplates"
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            onChange={this.handleTaskTemplateChange}
                            onSearch={this.handleTaskTemplateSearch}
                            filterOption={false}
                            allowClear
                          >
                            {this.state.searchedTemplates && this.state.searchedTemplates.map(e => (
                              <Select.Option key={e.id} value={e.id}>
                                {e.task_name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                    {localStorage.m8_auto_upload_emr === 'EMA' && this.state.apiCredentials
                      && (
                        <Form.Item
                          label="Facility (Optional)"
                          style={{ flex: 0.4 }}
                        >
                          <Select
                            showSearch
                            placeholder="Facility"
                            value={this.state.facilityName}
                            optionFilterProp="children"
                            onChange={this.handleFacilityChange}
                            onSearch={this.handleFacilitySearch}
                            filterOption={false}
                            allowClear
                          >
                            {this.state.searchedFacilities.map(facility => (
                              <Select.Option key={facility.id} value={facility.name}>
                                {facility.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                  </div>
                  {autoUploadEmr !== 'Kareo' && (
                    <>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Form.Item
                          label="Task"
                          style={{ flex: 0.35 }}
                        >
                          <Select
                            showSearch
                            placeholder="Task"
                            value={this.state.task}
                            optionFilterProp="children"
                            onChange={e => this.handleSelectionChange(e, 'task')}
                            onSearch={this.handleTaskSearch}
                            filterOption={false}
                          >
                            {this.state.searchedTasks.map(e => (
                              <Select.Option key={e} value={e}>
                                {e}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          label="Task Details"
                          style={{ flex: 0.35 }}
                        >
                          <AutoComplete
                            placeholder="Task Details"
                            value={this.state.taskDetails}
                            options={this.state.taskDetailsSearchOptions.map(option => ({ value: option }))}
                            onSearch={this.handleSearchTaskDetails}
                            onSelect={e => this.handleSelectionChange(e, 'taskDetails')}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Due Date"
                          style={{ flex: 0.3 }}
                        >
                          <DatePicker
                            onChange={this.handleDueDateChange}
                            name="dueDate"
                            format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                            value={this.state.dueDate && moment(this.state.dueDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                          />
                        </Form.Item>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {this.renderAssignToSelect()}
                        <Form.Item
                          label="Priority"
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            allowClear={false}
                            value={this.state.priority}
                            options={priorityOptions.map(option => ({ value: option }))}
                            onChange={e => this.handleSelectionChange(e, 'priority')}
                          />
                        </Form.Item>
                      </div>
                    </>
                  )}
                  {autoUploadEmr === 'Kareo' && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {this.renderAssignToSelect()}
                    </div>
                  )}
                  <Form.Item
                    name="notes"
                    label="Notes (optional)"
                  >
                    <Input.TextArea
                      onChange={this.handleNotesChange}
                      placeholder="Notes (optional)"
                      name="notes"
                      value={this.state.notes}
                    />
                  </Form.Item>
                </>
              )}
            {/* Authorization Section - Only show for EMA users with feature flag enabled */}
            {autoUploadEmr === 'EMA' && this.state.canExtractAuthorization && (
              <>
                <Divider />
                <Form.Item>
                  <Checkbox
                    checked={this.state.addAuthorization}
                    onChange={(e) => {
                      this.setState({ addAuthorization: e.target.checked });
                      if (!e.target.checked) {
                        // Reset authorization fields when unchecked
                        this.setState({
                          authorizationType: '',
                          authorizationNumber: '',
                          authorizationDescription: '',
                          authorizationActiveStatus: true,
                          authorizationUnlimitedVisits: false,
                          authorizationNumberOfVisits: '',
                          authorizationFromDate: '',
                          authorizationToDate: '',
                          authorizationNote: '',
                          authorizationReferringProvider: '',
                        });
                      }
                    }}
                  >
                    Add Insurance Authorization
                  </Checkbox>
                </Form.Item>
                {this.state.addAuthorization && (
                  <>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>Authorization Type <span style={{ color: 'red' }}>*</span></span>}
                        style={{ flex: 0.5 }}
                      >
                        <Select
                          placeholder="Select type"
                          value={this.state.authorizationType || undefined}
                          onChange={(value) => this.setState({ authorizationType: value })}
                        >
                          {authorizationTypeOptions.map((option) => (
                            <Select.Option key={option} value={option}>
                              {option}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label={<span>Authorization Number <span style={{ color: 'red' }}>*</span></span>}
                        style={{ flex: 0.5 }}
                      >
                        <Input
                          placeholder="Authorization number"
                          value={this.state.authorizationNumber}
                          onChange={(e) => this.setState({ authorizationNumber: e.target.value })}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label={<span>Description <span style={{ color: 'red' }}>*</span></span>}
                    >
                      <Input
                        placeholder="Authorization description"
                        value={this.state.authorizationDescription}
                        onChange={(e) => this.setState({ authorizationDescription: e.target.value })}
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                      {!this.state.authorizationUnlimitedVisits && (
                        <Form.Item
                          label={<span>Number of Visits <span style={{ color: 'red' }}>*</span></span>}
                          style={{ flex: 0.3 }}
                        >
                          <Input
                            type="number"
                            placeholder="Visits"
                            value={this.state.authorizationNumberOfVisits}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 99)) {
                                this.setState({ authorizationNumberOfVisits: value });
                              }
                            }}
                            min={1}
                            max={99}
                          />
                        </Form.Item>
                      )}
                      <Form.Item
                        label="Status"
                        style={{ flex: 0.35 }}
                      >
                        <Select
                          value={this.state.authorizationActiveStatus ? 'Active' : 'Inactive'}
                          onChange={(value) => this.setState({ authorizationActiveStatus: value === 'Active' })}
                        >
                          <Select.Option value="Active">Active</Select.Option>
                          <Select.Option value="Inactive">Inactive</Select.Option>
                        </Select>
                      </Form.Item>
                      <Form.Item style={{ flex: 0.35, marginBottom: '24px' }}>
                        <Checkbox
                          checked={this.state.authorizationUnlimitedVisits}
                          onChange={(e) => {
                            this.setState({
                              authorizationUnlimitedVisits: e.target.checked,
                              // Clear number of visits when unlimited is checked
                              authorizationNumberOfVisits: e.target.checked ? '' : this.state.authorizationNumberOfVisits,
                            });
                          }}
                        >
                          Unlimited Visits
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>From Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                        style={{ flex: 0.5 }}
                      >
                        <DatePicker
                          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                          value={this.state.authorizationFromDate ? moment(this.state.authorizationFromDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                          onChange={(_, dateString) => this.setState({ authorizationFromDate: dateString })}
                          placeholder="From date"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item
                        label={<span>To Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                        style={{ flex: 0.5 }}
                      >
                        <DatePicker
                          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                          value={this.state.authorizationToDate ? moment(this.state.authorizationToDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                          onChange={(_, dateString) => this.setState({ authorizationToDate: dateString })}
                          placeholder="To date"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label={
                        (
                          <span>
                            Referring Provider (optional)
                            {this.state.apiCredentials && (
                              <Tooltip title="Start typing to search and select a matching provider from your EMR.">
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </span>
                        )
                      }
                    >
                      {this.state.apiCredentials ? (
                        <ReferringProviderAutocomplete
                          placeholder="Type to search providers"
                          name="authorizationReferringProvider"
                          value={this.state.authorizationReferringProvider}
                          onChange={(providerId, providerName) => this.setState({ authorizationReferringProvider: providerName })}
                        />
                      ) : (
                        <Input
                          placeholder="Referring provider"
                          value={this.state.authorizationReferringProvider}
                          onChange={(e) => this.setState({ authorizationReferringProvider: e.target.value })}
                        />
                      )}
                    </Form.Item>
                    <Form.Item
                      label="Note (optional)"
                    >
                      <Input
                        placeholder="Authorization note"
                        value={this.state.authorizationNote}
                        onChange={(e) => this.setState({ authorizationNote: e.target.value })}
                      />
                    </Form.Item>
                  </>
                )}
              </>
            )}
          </Form>
        </ModalWrapper>
      </div>
    );
  }
}

AutoUpload.defaultProps = {
  allEmaTaskTemplates: {},
  allKareoTaskProviders: [],
  allMarketingRepresentatives: [],
  isLoading: false,
};

AutoUpload.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  refreshCurrentRecords: PropTypes.func.isRequired,
  triggerAutoUploadAndTaskCreation: PropTypes.func.isRequired,
  allDocumentCategories: PropTypes.object.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allKareoTaskProviders: PropTypes.array,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  getEmaPatients: PropTypes.func.isRequired,
  getTebraPatients: PropTypes.func.isRequired,
  getMarketingRepresentatives: PropTypes.func.isRequired,
  allEmaTaskTemplates: PropTypes.object,
  allMarketingRepresentatives: PropTypes.array,
  errorMessage: PropTypes.string,
  error: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default connect(state => ({
  allDocumentCategories: state.userProfile.allDocumentCategories,
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  allMarketingRepresentatives: state.userProfile.allMarketingRepresentatives,
  error: state.records.error,
  errorMessage: state.records.errorMessage,
  apiCredentials: state.records.apiCredentials,
  isLoading: state.records.isLoading,
}))(AutoUpload);
